/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/ServiceAccountAuthComponent" />
import * as React from "react";
import * as String from "VSS/Core/Util/String";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ObservableValue } from "VSS/Core/Observable";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { TextField } from "VSSUI/TextField";
import { FormItem } from "VSSUI/FormItem";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import * as Utils_String from "VSS/Core/Util/String";
import { InputDataType } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { EndpointsEditorPanelUtils, cloneEndpoint } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { KubernetesClusterHelper } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/KubernetesEndpointHelper";
import { ParseServiceAccountSecretYamlResponse } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/KubernetesEndpointContracts";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { ServiceAccountCommand, ServerUrlCommand, AccountCredsCommand } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/KubernetesEndpointConstants";
import { InputDetails } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { EndpointEditorBaseComponent, IEndpointEditorBaseComponentState, IEndpointEditorBaseComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointEditorBaseComponent";

export interface IServiceAccountAuthComponentProps extends IEndpointEditorBaseComponentProps {
    updateValidAndDirtyStateChange: (isValid?: boolean, isDirty?: boolean) => void;
}

export interface IKubernetesEndpointEditorComponentState extends IEndpointEditorBaseComponentState { }

export class ServiceAccountAuthComponent extends EndpointEditorBaseComponent<IServiceAccountAuthComponentProps, IKubernetesEndpointEditorComponentState>{

    constructor(props: IServiceAccountAuthComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._draftServiceEndpoint = cloneEndpoint(this.props.draftEndpoint);
        this._isNewServiceAccountUI = true;
        this._secretYaml = "";

        this._inputDetailsList = [
            {
                inputId: this._endpointUrlInputId,
                initialValue: props.draftEndpoint.url,
                inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.Uri, true),
                isConfidential: false,
                isUpdate: this.props.isUpdateForm
            }
        ];

        if (this._isNewServiceAccountUI) {
            this._inputDetailsList.push(
                {
                    inputId: this._secretInputId,
                    initialValue: this._secretYaml,
                    inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true),
                    isConfidential: true,
                    isUpdate: this.props.isUpdateForm
                }
            );
        }
        else {
            this._inputDetailsList.push(
                {
                    inputId: this._tokenInputId,
                    initialValue: props.draftEndpoint.authorization.parameters.apitoken,
                    inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true),
                    isConfidential: true,
                    isUpdate: this.props.isUpdateForm
                }
            );

            this._inputDetailsList.push(
                {
                    inputId: this._certificateInputId,
                    initialValue: props.draftEndpoint.authorization.parameters.serviceAccountCertificate,
                    inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true),
                    isConfidential: true,
                    isUpdate: this.props.isUpdateForm
                }
            );
        }
    }

    public componentDidMount() {
        this.addInputs(this._inputDetailsList);
    }

    public componentWillReceiveProps(props: IServiceAccountAuthComponentProps) {
        this._draftServiceEndpoint = cloneEndpoint(props.draftEndpoint);
    }

    public componentWillUnmount() {
        let currentInputIds: string[] = [];
        this._inputDetailsList.forEach((input) => currentInputIds.push(input.inputId));
        this.removeInputs(currentInputIds);
    }

    public render(): React.ReactNode {
        let urlObservable: ObservableValue<string> = new ObservableValue(this._draftServiceEndpoint.url || "");
        let tokenObservable: ObservableValue<string> = new ObservableValue(this._draftServiceEndpoint.authorization.parameters.apitoken || "");
        let certificateObservable: ObservableValue<string> = new ObservableValue(this._draftServiceEndpoint.authorization.parameters.serviceAccountCertificate || "");
        let secretYamlObservable: ObservableValue<string> = new ObservableValue(this._secretYaml);
        return (
            <React.Fragment>
                <FormItem
                    label={Resources.ServerUrl}
                    className="default-input-margin-top"
                    message={this._getErrorMessage(this._endpointUrlInputId)}
                    error={this._hasError(this._endpointUrlInputId)}>
                    {this._getSuggestionCommandMessage(Resources.CommandMessageServerUrl, ServerUrlCommand, Resources.CopyLabelForServerUrlCommand)}
                    <TextField
                        value={urlObservable}
                        onChange={(event, value) => {
                            urlObservable.value = value;
                        }}
                        onBlur={() => {
                            this._onUrlChange(urlObservable.value);
                        }}
                    />
                </FormItem>
                <span role="heading" aria-level={4} className="service-endpoint-section-headers">{Resources.Authorization}</span>
                {!this._isNewServiceAccountUI ?
                    <React.Fragment>
                        <FormItem
                            label={Resources.Token}
                            className="default-input-margin-top"
                            message={this._getErrorMessage(this._tokenInputId)}
                            error={this._hasError(this._tokenInputId)}>
                            {this._getSuggestionCommandMessage(Resources.CommandMessageToken, ServiceAccountCommand, Resources.CopyLabelForServiceAccountCommand)}
                            <TextField
                                value={tokenObservable}
                                placeholder={this.props.isUpdateForm ? "******" : ""}
                                onChange={(event, value) => {
                                    tokenObservable.value = value;
                                }}
                                onBlur={() => {
                                    this._onTokenChange(tokenObservable.value);
                                }}
                            />
                        </FormItem>
                        <FormItem
                            label={Resources.Certificate}
                            className="default-input-margin-top"
                            message={this._getErrorMessage(this._certificateInputId)}
                            error={this._hasError(this._certificateInputId)}>
                            {this._getSuggestionCommandMessage(Resources.CommandMessageCertificate, AccountCredsCommand,  Resources.CopyLabelForAccountCredsCommand)}
                            <TextField
                                value={certificateObservable}
                                placeholder={this.props.isUpdateForm ? "******" : ""}
                                multiline={true}
                                rows={4}
                                onChange={(event, value) => {
                                    certificateObservable.value = value;
                                }}
                                onBlur={() => {
                                    this._onCertificateChange(certificateObservable.value);
                                }}
                            />
                        </FormItem>
                    </React.Fragment>
                    : <FormItem
                        label={Resources.KubernetesServiceAccountSecretYaml}
                        className="default-input-margin-top"
                        message={this._getErrorMessage(this._secretInputId)}
                        error={this._hasError(this._secretInputId)}>
                        <span className={"fontSizeS flex-row secondary-text"}>{Resources.ServiceAccountSecretSuggestionHeading}</span>
                        {this._getSuggestionCommandMessage(Resources.ServiceAccountSuggestionFormat, ServiceAccountCommand, Resources.CopyLabelForServiceAccountCommand)}
                        {this._getSuggestionCommandMessage(Resources.ServiceAccountSecretSuggestionFormat, AccountCredsCommand, Resources.CopyLabelForAccountCredsCommand)}
                        <TextField
                            value={secretYamlObservable}
                            placeholder={this.props.isUpdateForm ? "******" : ""}
                            multiline={true}
                            rows={4}
                            onChange={(event, value) => {
                                secretYamlObservable.value = value;
                                this._onSecretChange(secretYamlObservable.value);
                            }}
                        />
                    </FormItem>
                }
            </React.Fragment>
        );
    }

    private _populateTokenAndCertificateFromSecretYaml(serviceAccountSecretYaml: string): void {
        if (!!serviceAccountSecretYaml) {
            KubernetesClusterHelper.getTokenAndCertificateFromSecretYaml(serviceAccountSecretYaml).then((result: ParseServiceAccountSecretYamlResponse) => {
                this._draftServiceEndpoint.authorization.parameters.apitoken = result.token;
                this._draftServiceEndpoint.authorization.parameters.serviceAccountCertificate = result.certificate;
                this._draftServiceEndpoint.authorization.parameters.isCreatedFromSecretYaml = true.toString();
                this.props.updateDraftEndpointState(this._draftServiceEndpoint);
            }, (error: any) => {
                this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                this.setInputError(this._secretInputId, Utils_String.localeFormat(Resources.ServiceAccountSecretParsingFailed, error));
            });
        }
    }

    private _getSuggestionCommandMessage(format: string, command: string, copyLabel: string ): React.ReactNode {
        return (
            <FormatComponent format={format} className={"body-s flex-row nowrap secondary-text"} elementType={"div"} >
                <div className={"shell-command fontSizeS flex-row secondary-text"}>
                    <div className={"overflow-ellipsis command-text"}>{command}</div>
                    <Button tooltipProps={{ text: Resources.CopyCommand }} onClick={() => EndpointsEditorPanelUtils.copyToClipboard(command)} ariaLabel={copyLabel}>
                        <Icon iconName={"Copy"} className="secondary-text" />
                    </Button>
                </div>
            </FormatComponent>
        );
    }       

    private _hasError(inputId: string): boolean {
        return (!!this.getInputError(inputId));
    }

    private _getErrorMessage(inputId: string): string {
        return this.getInputError(inputId) || "";
    }

    private _onUrlChange = (value: any) => {
        this._draftServiceEndpoint.url = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._endpointUrlInputId, value);
    }

    private _onTokenChange = (value: any) => {
        this._draftServiceEndpoint.authorization.parameters.apitoken = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._tokenInputId, value);
    }

    private _onCertificateChange = (value: any) => {
        this._draftServiceEndpoint.authorization.parameters.serviceAccountCertificate = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._certificateInputId, value);
    }

    private _onSecretChange = (value: any) => {
        this._secretYaml = value;
        this._populateTokenAndCertificateFromSecretYaml(value);
        this.validateInputState(this._secretInputId, value);
    }

    protected handleValidAndDirtyStateChange(isValid?: boolean, isDirty?: boolean) {
        this.props.updateValidAndDirtyStateChange(isValid, isDirty);
    }

    private _draftServiceEndpoint: ServiceEndpoint;
    private _isNewServiceAccountUI: boolean;
    private _secretYaml: string;
    private _endpointUrlInputId = "endpoint-url";
    private _tokenInputId = "api-token";
    private _certificateInputId = "sa-certificate";
    private _secretInputId = "sa-secret";
    private _inputDetailsList: InputDetails[];
}
